/**
 * Internal deck render page for server-side PDF generation.
 * 
 * This page renders slides with:
 * - Canonical design system viewport
 * - No animations or transitions
 * - Page breaks between slides for PDF printing
 * - Ready signal for headless browser capture
 * 
 * Data is passed via:
 * - URL param: ?deck=<base64-encoded-json>
 * - Window injection: window.__DECK_DATA__ (for large decks)
 */

import { useEffect, useMemo, useState, Suspense, type CSSProperties } from 'react'
import { useSearchParams } from '@/routing/navigation'
import { MotionConfig } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import type { SlideData, Deck } from '@digital-twins/deck-ui/slides/contracts'
import {
  getSlideComponent,
  validateRegistryCoverage,
  ensureSlideRegistryLoaded,
} from '@digital-twins/deck-ui/slides/registry'
import { SlideErrorBoundary, NoDataFallback } from '@digital-twins/deck-ui/slides/SlideErrorBoundary'
import { TextRenderModeContext } from '@digital-twins/deck-ui/components/text/TextClamp'
import { validateSlideData } from '@/lib/validation/slide-sanitizer'
import slideDimensions from '../../../../../../../config/slide_dimensions.json'
import { prepareDeckForRender } from '@/features/studies/deck/render/prepareDeckForRender'
import { applyDeckMetadataBranding } from '@/features/studies/deck/render/deckMetadataBranding'
import i18n, { resolveUiLanguage } from '@/i18n'
import { useSlideErrorBoundaryLabels } from '@/features/studies/report/page/components/deck/deckChromeLabels'

// Note: Canonical slide components are dynamically loaded through the registry.

// Canonical slide dimensions (single source of truth)
const SLIDE_WIDTH = slideDimensions.width
const SLIDE_HEIGHT = slideDimensions.height

// PDF export waits on [data-deck-ready="true"]. A hung image.decode() used to
// block that flag forever and 502 the download. Each wait is bounded. The
// budget is their sum — Recharts and Chart.js run one after another — so the
// flag cannot flip while a chart is still painting.
const FONT_READY_TIMEOUT_MS = 3000
const IMAGE_DECODE_TIMEOUT_MS = 2000
const CHART_PAINT_TIMEOUT_MS = 15000
const ASSET_READY_BUDGET_MS =
  FONT_READY_TIMEOUT_MS + IMAGE_DECODE_TIMEOUT_MS + CHART_PAINT_TIMEOUT_MS * 2

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => resolve(undefined), ms)
    promise.then(
      (value) => {
        window.clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        window.clearTimeout(timer)
        reject(err)
      },
    )
  })
}

const baseFallbackFrameStyle: CSSProperties = {
  width: SLIDE_WIDTH,
  height: SLIDE_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const errorFallbackStyle: CSSProperties = {
  ...baseFallbackFrameStyle,
  background: 'hsl(var(--destructive))',
  color: 'hsl(var(--destructive-foreground))',
}

const waitingFallbackStyle: CSSProperties = {
  ...baseFallbackFrameStyle,
  background: 'hsl(var(--card))',
  color: 'hsl(var(--foreground))',
}

/**
 * The PDF export always renders static. The visual QA runner alone sets
 * `window.__DECK_RENDER_MODE__ = 'interactive'` to screenshot the same slides
 * as the report draws them (plots plan LB: the chart label (i)).
 */
function deckRenderMode(): 'static' | 'interactive' {
  return typeof window !== 'undefined' && (window as any).__DECK_RENDER_MODE__ === 'interactive' ? 'interactive' : 'static'
}

// Slide renderer without animations
function StaticSlideRenderer({ slide, deck }: { slide: SlideData; deck?: Deck }) {
  // The fallbacks are app chrome: the app's translations, in its current UI language.
  const { t } = useTranslation('app')
  const slideErrorLabels = useSlideErrorBoundaryLabels()
  const baseProps = { isActive: true, editable: false, renderMode: deckRenderMode(), deck }

  const validationResult = validateSlideData(slide)
  if (!validationResult.valid) {
    return (
      <NoDataFallback
        title={(slide as any).title || slide.type.replace(/-/g, ' ')}
        message={t('studyReport.slideCouldNotDisplay')}
        suggestion={t('studyReport.slideRefreshSuggestion')}
      />
    )
  }

  // Use sanitized data for rendering (truncated arrays, clamped values)
  const sanitizedSlide = validationResult.sanitizedData

  const Component = getSlideComponent(sanitizedSlide.type)
  if (!Component) {
    return (
      <NoDataFallback
        title={(sanitizedSlide as any).title || sanitizedSlide.type}
        message={t('studyReport.slideNotAvailableYet')}
        suggestion={t('studyReport.slideRegenerateSuggestion')}
      />
    )
  }

  return (
    <SlideErrorBoundary labels={slideErrorLabels}>
      {/* Every clamp on the slide follows this page's render mode: a static render
          draws complete text whole (wave D), whether or not its call site passes it. */}
      <TextRenderModeContext.Provider value={baseProps.renderMode}>
        <Component {...sanitizedSlide} {...baseProps} />
      </TextRenderModeContext.Provider>
    </SlideErrorBoundary>
  )
}

function DeckRenderContent() {
  const searchParams = useSearchParams()
  const [deck, setDeck] = useState<Deck | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [assetsReady, setAssetsReady] = useState(false)
  const [registryReady, setRegistryReady] = useState(false)
  const [registryError, setRegistryError] = useState<string | null>(null)
  const deckForRender = useMemo(() => {
    if (!deck) return null
    return prepareDeckForRender(deck)
  }, [deck])

  useEffect(() => {
    if (!deckForRender) {
      return
    }
    setRegistryReady(false)
    setRegistryError(null)
    ensureSlideRegistryLoaded()
      .then(() => {
        setRegistryReady(true)
      })
      .catch((err) => {
        setRegistryError((err as Error)?.message || String(err))
      })
  }, [deckForRender])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !registryReady) {
      return
    }
    const { missing, extra } = validateRegistryCoverage()
    if (missing.length > 0) {
      console.warn('[SlideRegistry] Missing registered slide types:', missing)
    }
    if (extra.length > 0) {
      console.warn('[SlideRegistry] Extra registered slide types:', extra)
    }
  }, [registryReady])

  // The URL-param source. Keyed on the param STRING, not on `deck`.
  //
  // This used to share one effect with the window/event source below, whose
  // deps are `[searchParams, deck]`. On the `?deck=` path that is a loop: the
  // effect parses the param and calls `setDeck` with a freshly parsed object,
  // `deck` is one of its own dependencies, so it re-runs, parses again, and
  // sets another new object identity -- for ever. React ends it with "Maximum
  // update depth exceeded" and the page renders blank or hangs on "Loading
  // slide components...".
  //
  // That is why this page could not reliably render a deck from a URL, which
  // makes it useless for exactly what it exists for. Splitting the two sources
  // fixes it: a URL param is parsed once per param value, and a primitive
  // string is a stable dependency where a parsed object never can be.
  const deckParam = searchParams.get('deck')
  useEffect(() => {
    if (!deckParam) return
    try {
      setDeck(JSON.parse(atob(deckParam)) as Deck)
    } catch (e) {
      setError(`Failed to parse deck data: ${e}`)
    }
  }, [deckParam])

  // The window / event / poll source, for decks too large to put in a URL.
  useEffect(() => {
    if (deckParam) {
      return
    }

    const windowDeck = (window as any).__DECK_DATA__
    if (windowDeck) {
      setDeck(windowDeck)
      return
    }

    const handleDeckData = (e: CustomEvent) => {
      if (e.detail) {
        setDeck(e.detail as Deck)
      }
    }

    window.addEventListener('deckDataReady' as any, handleDeckData)

    const pollInterval = setInterval(() => {
      const data = (window as any).__DECK_DATA__
      if (data && !deck) {
        setDeck(data)
        clearInterval(pollInterval)
      }
    }, 100)

    return () => {
      window.removeEventListener('deckDataReady' as any, handleDeckData)
      clearInterval(pollInterval)
    }
  }, [deckParam, deck])

  // The exported PDF has no viewer, so the chrome around a slide (an error or empty
  // state) reads in the deck's own language, as its slide copy does; a language the
  // app has no translations for reads English.
  const deckLocale = deckForRender?.metadata?.canonicalDeck?.locale
  useEffect(() => {
    if (deckLocale) void i18n.changeLanguage(resolveUiLanguage(deckLocale))
  }, [deckLocale])

  // Apply deck-scoped brand tokens from metadata so headless PDF rendering has no auth dependency.
  useEffect(() => {
    if (!deckForRender) return
    return applyDeckMetadataBranding(deckForRender.metadata, 'light')
  }, [deckForRender?.metadata])

  useEffect(() => {
    if (!deckForRender) return
    let cancelled = false

    const waitForCondition = (predicate: () => boolean, timeoutMs = CHART_PAINT_TIMEOUT_MS) => {
      return new Promise<boolean>((resolve) => {
        let resolved = false
        const check = () => {
          if (resolved) return
          if (predicate()) {
            resolved = true
            cleanup()
            resolve(true)
          }
        }
        const observer = new MutationObserver(check)
        observer.observe(document.body, { subtree: true, childList: true, attributes: true })
        const raf = () => {
          check()
          if (!resolved) requestAnimationFrame(raf)
        }
        const timeout = setTimeout(() => {
          cleanup()
          resolve(false)
        }, timeoutMs)
        const cleanup = () => {
          observer.disconnect()
          clearTimeout(timeout)
        }
        check()
        requestAnimationFrame(raf)
      })
    }

    const markAssetsReady = () => {
      if (!cancelled) setAssetsReady(true)
    }

    const waitForPaint = async () => {
      if (document.fonts?.ready) {
        try {
          await withTimeout(document.fonts.ready, FONT_READY_TIMEOUT_MS)
        } catch {
          // fonts.ready rejected — capture with fallback fonts
        }
      }

      const images = Array.from(document.querySelectorAll('img'))
      await Promise.all(
        images.map(async (img) => {
          try {
            if (img.complete && img.naturalHeight > 0) return
            if ('decode' in img) {
              await withTimeout(img.decode(), IMAGE_DECODE_TIMEOUT_MS)
            }
          } catch {
            // Continue even if decode fails
          }
        })
      )

      const hasRecharts = document.querySelectorAll('.recharts-wrapper').length > 0
      if (hasRecharts) {
        await waitForCondition(() =>
          Array.from(document.querySelectorAll('.recharts-wrapper svg'))
            .every((svg) => svg.querySelector('path, rect, circle, line, polygon'))
        )
      }

      const hasChartJs = document.querySelectorAll('[data-chart-library="chartjs"]').length > 0
      if (hasChartJs) {
        await waitForCondition(() => {
          const wrappers = Array.from(document.querySelectorAll('[data-chart-library="chartjs"]'))
          if (wrappers.length === 0) return true
          const canvases = wrappers.flatMap((w) => Array.from(w.querySelectorAll('canvas')))
          if (canvases.length === 0) return false
          return canvases.every((c) => (c as HTMLCanvasElement).dataset.chartReady === 'true')
        })
      }
    }

    const checkAssets = async () => {
      // One hung persona/brand image decode used to block data-deck-ready forever,
      // which the PDF sidecar treats as a hard 502. The budget covers every bounded
      // wait; ready flips only after that settles, so a chart still painting is
      // not captured.
      try {
        await withTimeout(waitForPaint(), ASSET_READY_BUDGET_MS)
      } finally {
        markAssetsReady()
      }
    }

    void checkAssets()
    return () => {
      cancelled = true
    }
  }, [deckForRender])

  useEffect(() => {
    if (deckForRender && assetsReady && registryReady) {
      setReady(true)
    }
  }, [deckForRender, assetsReady, registryReady])

  if (error) {
    return (
      <div style={errorFallbackStyle}>
        <p style={{ fontSize: 24 }}>{error}</p>
      </div>
    )
  }

  if (registryError) {
    return (
      <div style={errorFallbackStyle}>
        <p style={{ fontSize: 24 }}>{registryError}</p>
      </div>
    )
  }

  if (!deckForRender) {
    return (
      <div style={waitingFallbackStyle}>
        <p style={{ fontSize: 24 }}>Waiting for deck data...</p>
      </div>
    )
  }

  if (!registryReady) {
    return (
      <div style={waitingFallbackStyle}>
        <p style={{ fontSize: 24 }}>Loading slide components...</p>
      </div>
    )
  }

  return (
    <MotionConfig reducedMotion="always">
      <div 
        data-deck-ready={ready ? "true" : "false"}
        className="deck-render-container"
        style={{ 
          width: 'var(--slide-max-width)',
          margin: 0,
          padding: 0,
          ['--slide-max-width' as any]: `${SLIDE_WIDTH}px`,
          ['--slide-height' as any]: `${SLIDE_HEIGHT}px`,
        }}
      >
        {deckForRender.slides.map((slide, index) => (
        <div
          key={index}
          data-slide-index={index}
          data-slide-ready={ready ? "true" : "false"}
          className="slide-page"
          style={{
            width: 'var(--slide-max-width)',
            height: 'var(--slide-height)',
            minWidth: 'var(--slide-max-width)',
            minHeight: 'var(--slide-height)',
            maxWidth: 'var(--slide-max-width)',
            maxHeight: 'var(--slide-height)',
            pageBreakAfter: 'always',
            pageBreakInside: 'avoid',
            overflow: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
          }}
        >
          <div 
            className="slide-inner" 
            style={{ 
              width: 'var(--slide-max-width)', 
              height: 'var(--slide-height)',
              minWidth: 'var(--slide-max-width)',
              minHeight: 'var(--slide-height)',
              position: 'relative',
              boxSizing: 'border-box',
            }}
          >
            <StaticSlideRenderer slide={slide} deck={deckForRender} />
          </div>
        </div>
        ))}
      </div>
    </MotionConfig>
  )
}

// CSS for PDF rendering - defined as a constant to avoid template literal issues
const pdfRenderStyles = `
  /* Noto Sans JP — CJK fallback for Japanese deck slides.
     Loaded from Google Fonts CDN; must be declared before other @font-face
     rules so it is available as a fallback for any slide theme font. */
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;400;500;700&display=swap');

  /* Self-hosted Inter fonts for PDF rendering */
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/inter-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 500;
    font-display: block;
    src: url('/fonts/inter-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 600;
    font-display: block;
    src: url('/fonts/inter-600.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 700;
    font-display: block;
    src: url('/fonts/inter-600.woff2') format('woff2');
  }

  /* Self-hosted IBM Plex Sans for PDF rendering */
  @font-face {
    font-family: 'IBM Plex Sans';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/ibm-plex-sans-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'IBM Plex Sans';
    font-style: normal;
    font-weight: 600;
    font-display: block;
    src: url('/fonts/ibm-plex-sans-600.woff2') format('woff2');
  }

  /* Self-hosted Source Serif 4 for PDF rendering */
  @font-face {
    font-family: 'Source Serif 4';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/source-serif-4-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Source Serif 4';
    font-style: normal;
    font-weight: 600;
    font-display: block;
    src: url('/fonts/source-serif-4-600.woff2') format('woff2');
  }

  /* Self-hosted DM Sans for PDF rendering */
  @font-face {
    font-family: 'DM Sans';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/dm-sans-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'DM Sans';
    font-style: normal;
    font-weight: 600;
    font-display: block;
    src: url('/fonts/dm-sans-600.woff2') format('woff2');
  }

  /* Self-hosted Roboto for PDF rendering */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/roboto-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    font-display: block;
    src: url('/fonts/roboto-600.woff2') format('woff2');
  }

  /* Self-hosted Montserrat (heading) for PDF rendering */
  @font-face {
    font-family: 'Montserrat';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/montserrat-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Montserrat';
    font-style: normal;
    font-weight: 700;
    font-display: block;
    src: url('/fonts/montserrat-700.woff2') format('woff2');
  }

  /* Self-hosted Sumana (Fairgen default heading font) for PDF rendering */
  @font-face {
    font-family: 'Sumana';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/sumana-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Sumana';
    font-style: normal;
    font-weight: 700;
    font-display: block;
    src: url('/fonts/sumana-700.woff2') format('woff2');
  }

  /* Self-hosted Playfair Display for PDF rendering */
  @font-face {
    font-family: 'Playfair Display';
    font-style: normal;
    font-weight: 400;
    font-display: block;
    src: url('/fonts/playfair-display-400.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Playfair Display';
    font-style: normal;
    font-weight: 700;
    font-display: block;
    src: url('/fonts/playfair-display-700.woff2') format('woff2');
  }

  /* Reset everything */
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: var(--slide-bg) !important;
    overflow-x: hidden !important;
    width: var(--slide-max-width) !important;
  }
  
  /* Disable all animations for consistent renders */
  *, *::before, *::after {
    animation: none !important;
    animation-delay: 0s !important;
    animation-duration: 0s !important;
    transition: none !important;
    transition-delay: 0s !important;
    transition-duration: 0s !important;
  }
  
  /* Force slide container and immediate children to exact dimensions */
  .slide-page {
    width: var(--slide-max-width) !important;
    height: var(--slide-height) !important;
    min-width: var(--slide-max-width) !important;
    min-height: var(--slide-height) !important;
    max-width: var(--slide-max-width) !important;
    max-height: var(--slide-height) !important;
    overflow: hidden !important;
    position: relative !important;
  }
  
  .slide-inner {
    width: var(--slide-max-width) !important;
    height: var(--slide-height) !important;
    min-width: var(--slide-max-width) !important;
    min-height: var(--slide-height) !important;
    position: relative !important;
    display: block !important;
  }
  
  /* Force the slide component root (direct child of slide-inner) to fill completely */
  .slide-inner > * {
    width: var(--slide-max-width) !important;
    height: var(--slide-height) !important;
    min-width: var(--slide-max-width) !important;
    min-height: var(--slide-height) !important;
    max-width: var(--slide-max-width) !important;
    max-height: var(--slide-height) !important;
  }
  
  /* Force all Tailwind h-full elements to be 100% of their parent */
  .slide-inner .h-full {
    height: 100% !important;
    min-height: 100% !important;
  }
  
  .slide-inner .w-full {
    width: 100% !important;
    min-width: 100% !important;
  }
  
  /* Ensure flex containers fill properly */
  .slide-inner .flex {
    display: flex !important;
  }
  
  .slide-inner .flex-col {
    flex-direction: column !important;
  }
  
  /* Aspect ratio containers should fill */
  .slide-inner .aspect-video {
    aspect-ratio: 16/9 !important;
    width: 100% !important;
    height: auto !important;
  }
  
  /* Handle absolute positioning within slides */
  .slide-inner .absolute.inset-0 {
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }
  
  /* ResponsiveContainer from Recharts needs explicit dimensions */
  .slide-inner .recharts-responsive-container {
    width: 100% !important;
    height: 100% !important;
  }
  
  /* PDF page settings */
  @page {
    size: ${SLIDE_WIDTH}px ${SLIDE_HEIGHT}px;
    margin: 0;
  }
  
  @media print {
    html, body {
      width: ${SLIDE_WIDTH}px !important;
      height: auto !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    .deck-render-container {
      width: ${SLIDE_WIDTH}px !important;
    }
    
    .slide-page {
      page-break-after: always !important;
      page-break-inside: avoid !important;
      break-after: page !important;
      break-inside: avoid !important;
    }
    
    /* Last slide shouldn't have page break after */
    .slide-page:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
  }
`

export default function DeckRenderPage() {
  return (
    <Suspense fallback={
      <div style={waitingFallbackStyle}>
        <p style={{ fontSize: 24 }}>Loading...</p>
      </div>
    }>
      <style dangerouslySetInnerHTML={{ __html: pdfRenderStyles }} />
      <DeckRenderContent />
    </Suspense>
  )
}
